import "./qa-premium.css";
import { useState } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { trpc } from "@/utils/trpc";
import { toast } from "sonner";
import { formatDate, QaCrumb } from "./qa-shared";
import { Building2, Calendar, User, Bell } from "lucide-react";

function shiftDate(iso: string, days: number): Date | null {
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + days);
  return d;
}

export default function QaScheduleVisit() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const { data: sites = [] } = trpc.sites.getAll.useQuery();
  const { data: templates = [] } = trpc.qa.templates.getAll.useQuery();
  const { data: users = [] } = trpc.users.getAll.useQuery();

  const [siteId, setSiteId] = useState("");
  const [visitDate, setVisitDate] = useState("");
  const [tplId, setTplId] = useState("");
  const [dcRep, setDcRep] = useState("");
  const [auditorInput, setAuditorInput] = useState("");
  const [leadInput, setLeadInput] = useState("");
  const [auditorId, setAuditorId] = useState("");
  const [leadId, setLeadId] = useState("");

  const schedule = trpc.qa.visits.schedule.useMutation({
    onSuccess: (data) => { toast.success(`Visit ${data.visitNumber} scheduled`); setLocation(`/qa/visits/${data.id}`); },
    onError: (e) => toast.error(e.message),
  });

  // Check if selected date is in the past
  const isPastDate = visitDate ? new Date(visitDate) < new Date(new Date().toISOString().split('T')[0]) : false;
  const canSubmit = !!(siteId && visitDate && !isPastDate);

  const siteObj = sites.find((s: any) => String(s.id) === siteId);

  const t14 = visitDate ? shiftDate(visitDate, -14) : null;
  const t10 = visitDate ? shiftDate(visitDate, -10) : null;
  const t7 = visitDate ? shiftDate(visitDate, -7) : null;

  const auditorObj = auditorId ? users.find((u: any) => String(u.id) === auditorId) : null;
  const leadObj = leadId ? users.find((u: any) => String(u.id) === leadId) : null;
  const tplObj = tplId ? templates.find((t: any) => String(t.id) === tplId) : null;

  const filteredAuditors = users.filter((u: any) => (u.name || "").toLowerCase().includes(auditorInput.toLowerCase()));
  const filteredLeads = users.filter((u: any) => (u.name || "").toLowerCase().includes(leadInput.toLowerCase()));

  const doSubmit = () => {
    // Format date to YYYY-MM-DD
    const dateObj = new Date(visitDate);
    const formattedDate = dateObj.toISOString().split('T')[0];

    schedule.mutate({
      siteId: Number(siteId),
      visitDate: formattedDate,
      templateId: tplId ? Number(tplId) : undefined,
      dcRepresentative: dcRep || undefined,
      auditorId: auditorId ? Number(auditorId) : undefined,
      leadId: leadId ? Number(leadId) : undefined,
    });
  };

  return (
    <div className="qa-premium full">
      <div className="sched-header">
        <div className="sched-header-inner">
          <div>
            <QaCrumb backTo="/qa/visits" items={[{ label: t("qa.visits.title"), to: "/qa/visits" }, { label: t("qa.scheduleVisit.title") }]} />
            <h1>{t("qa.scheduleVisit.title")}</h1>
            <p>{t("qa.scheduleVisit.subtitle")}</p>
          </div>
        </div>
      </div>

      <div className="wrap" style={{ paddingTop: 22 }}>
        <div className="sched">
          {/* FORM */}
          <div className="sform">
            <div className="sform-h">{t("qa.scheduleVisit.visitDetails")}</div>
            <div className="sform-sub">{t("qa.scheduleVisit.routingReminders")}</div>

            <div className="field">
              <label className="flabel">{t("qa.scheduleVisit.dataCentreSite")} <span className="req">*</span></label>
              <select className="fselect" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                <option value="">{t("qa.scheduleVisit.selectDcSite")}</option>
                {sites.map((s: any) => <option key={s.id} value={s.id}>{s.code ? `${s.code} — ${s.name}` : s.name}</option>)}
              </select>
            </div>

            <div className="field">
              <label className="flabel">{t("qa.scheduleVisit.proposedVisitDate")} <span className="req">*</span></label>
              <input type="date" className="finput" value={visitDate} onChange={(e) => setVisitDate(e.target.value)} min={new Date().toISOString().split('T')[0]} />
              {isPastDate && <div style={{color: '#D0021B', fontSize: '13px', marginTop: '6px', fontWeight: 500}}>{t("qa.scheduleVisit.pastDateWarning")}</div>}
            </div>



            <div className="field">
              <label className="flabel">{t("qa.scheduleVisit.dcOpsRepresentative")}</label>
              <input className="finput" value={dcRep} onChange={(e) => setDcRep(e.target.value)} placeholder={t("qa.scheduleVisit.dcRepPlaceholder")} />
            </div>

            <div className="sched-actions">
              <button className="btn btn-ghost" onClick={() => setLocation("/qa/visits")}>{t("qa.scheduleVisit.cancel")}</button>
              <button className="btn btn-primary" disabled={!canSubmit || schedule.isPending} onClick={doSubmit}>
                {schedule.isPending ? <i className="ti ti-loader-2" style={{ animation: "qa-spin .7s linear infinite" }} /> : <i className="ti ti-calendar-plus" />} {t("qa.scheduleVisit.scheduleVisit")}
              </button>
            </div>
          </div>

          {/* LIVE SUMMARY */}
          <div className="summary">
            <div className="sh">
              <div className="stt">{t("qa.scheduleVisit.visitSummary")}</div>
              <div className="sss">{t("qa.scheduleVisit.updatesLive")}</div>
            </div>
            <div className="sbody">
              <div className="sitem">
                <span className="si"><Building2 size={16} /></span>
                <div><div className="sk">{t("qa.scheduleVisit.dataCentre")}</div><div className={`sv ${siteObj ? "" : "muted"}`}>{siteObj ? (siteObj.code ? `${siteObj.code} — ${siteObj.name}` : siteObj.name) : t("qa.scheduleVisit.notSelected")}</div></div>
              </div>
              <div className="sitem">
                <span className="si"><Calendar size={16} /></span>
                <div><div className="sk">{t("qa.scheduleVisit.visitDate")}</div><div className={`sv ${visitDate ? "" : "muted"}`}>{visitDate ? formatDate(visitDate) : t("qa.scheduleVisit.notSet")}</div></div>
              </div>

              <div className="sitem">
                <span className="si"><User size={16} /></span>
                <div><div className="sk">{t("qa.scheduleVisit.dcRepresentative")}</div><div className={`sv ${dcRep ? "" : "muted"}`}>{dcRep || "—"}</div></div>
              </div>

              <div className="remind">
                <div className="rh"><Bell size={13} style={{ marginRight: 2 }} /> {t("qa.scheduleVisit.reminderSchedule")}</div>
                {visitDate ? (
                  <>
                    <div className="rr"><span className="rt"><span className="dot" /> T-14</span><span className="rd">{t14 ? formatDate(t14) : "—"}</span></div>
                    <div className="rr"><span className="rt"><span className="dot" /> T-10</span><span className="rd">{t10 ? formatDate(t10) : "—"}</span></div>
                    <div className="rr"><span className="rt"><span className="dot" /> T-7</span><span className="rd">{t7 ? formatDate(t7) : "—"}</span></div>
                  </>
                ) : (
                  <div className="rd-empty">{t("qa.scheduleVisit.pickVisitDate")}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
